import type {
  ComparisonOddsMap,
  OptimizerResult,
  SportsbookLiquidityPair,
  SportsbookOddsPair,
} from "@/types/feeds";
import { WEIGHTS_BY_SPORTSBOOK_NAME, normalizeFantasySportsbookId } from "@/types/feeds";
import type { SelectionSide } from "@/types/evbook";
import type { BetLinkSet } from "@/lib/bet-links";
import type { DisplayPlayerProp } from "./types";
import {
  applySingleSideOdds,
  setSportsbookOddsForBook,
  type OddsSlotContext,
} from "./book-odds-assignment";
import { toFiniteNumber } from "./transform-helpers";

interface ApplyComparisonMetadataArgs {
  prop: DisplayPlayerProp;
  result: OptimizerResult;
  rowSportsbook: string;
  rowSide: SelectionSide;
  primaryLine: number | null;
  oddsSlotContext: OddsSlotContext;
}

export function applyComparisonMetadata({
  prop,
  result,
  rowSportsbook,
  rowSide,
  primaryLine,
  oddsSlotContext,
}: ApplyComparisonMetadataArgs): void {
  const comparisonLines: Record<string, number> = {};
  const rawLiquidity = result.sportsbook_liquidity ?? {};
  const normalizedLiquidity: Record<string, SportsbookLiquidityPair> = {};
  Object.entries(rawLiquidity).forEach(([book, pair]) => {
    if (!pair) return;
    const normalizedBook = normalizeFantasySportsbookId(book);
    if (!normalizedBook) return;

    const overAmount =
      typeof pair.over === "number" && Number.isFinite(pair.over)
        ? pair.over
        : null;
    const underAmount =
      typeof pair.under === "number" && Number.isFinite(pair.under)
        ? pair.under
        : null;

    normalizedLiquidity[normalizedBook] = {
      over: overAmount,
      under: underAmount,
    };
  });
  if (Object.keys(normalizedLiquidity).length > 0) {
    prop.__sportsbook_liquidity = normalizedLiquidity;
  }

  const comparisonMap = result.comparison_odds ?? {};
  const comparisonEntries = Object.entries(comparisonMap);
  comparisonEntries.forEach(([book, entry]) => {
    const normalizedBook = normalizeFantasySportsbookId(book);
    if (!normalizedBook || normalizedBook === rowSportsbook) {
      return;
    }

    const american = toFiniteNumber(entry?.american_odds);
    if (american == null) {
      return;
    }

    const lineValue = toFiniteNumber(entry?.line);
    if (
      lineValue != null &&
      primaryLine != null &&
      Math.abs(lineValue - primaryLine) > 1e-6
    ) {
      comparisonLines[normalizedBook] = lineValue;
    }

    const selectionForBook = String(entry?.selection_side ?? rowSide).toUpperCase();
    const normalizedSelection =
      selectionForBook === "HOME"
        ? "OVER"
        : selectionForBook === "AWAY"
          ? "UNDER"
          : selectionForBook;

    if (normalizedSelection !== "OVER" && normalizedSelection !== "UNDER") {
      return;
    }

    applySingleSideOdds(prop, normalizedBook, american, normalizedSelection as "OVER" | "UNDER", oddsSlotContext);
  });

  const hasComparisonEntries = comparisonEntries.length > 0;

  const sportsbookOdds = result.sportsbook_odds ?? {};
  Object.entries(sportsbookOdds).forEach(([book, pair]) => {
    const normalizedBook = normalizeFantasySportsbookId(book);
    if (!normalizedBook || normalizedBook === rowSportsbook) {
      return;
    }

    const oddsPair = pair as SportsbookOddsPair | undefined;
    const overValue = toFiniteNumber(oddsPair?.over);
    const underValue = toFiniteNumber(oddsPair?.under);
    if (overValue == null && underValue == null) {
      return;
    }

    const rawLine = toFiniteNumber(oddsPair?.line);
    if (
      rawLine != null &&
      primaryLine != null &&
      Math.abs(rawLine - primaryLine) > 1e-6
    ) {
      comparisonLines[normalizedBook] = rawLine;
    }

    setSportsbookOddsForBook(prop, normalizedBook, overValue, underValue, oddsSlotContext);
  });

  if (!hasComparisonEntries) {

    const builtComparisonOdds: ComparisonOddsMap = {};
    Object.entries(sportsbookOdds).forEach(([book, pair]) => {
      const normalizedBook = normalizeFantasySportsbookId(book);
      if (!normalizedBook || normalizedBook === rowSportsbook) {
        return;
      }
      const oddsPair = pair as SportsbookOddsPair | undefined;
      const oddsValue =
        rowSide === "OVER"
          ? toFiniteNumber(oddsPair?.over)
          : toFiniteNumber(oddsPair?.under);
      if (oddsValue == null) {
        return;
      }
      builtComparisonOdds[normalizedBook] = {
        american_odds: oddsValue,
        selection_side: rowSide,
        line: toFiniteNumber(oddsPair?.line),
      };
    });
    if (Object.keys(builtComparisonOdds).length > 0) {
      prop.__comparison_odds_raw = builtComparisonOdds;
    }
  }

  if (hasComparisonEntries) {
    const normalizedComparisonKeys = new Set(
      Object.keys(comparisonMap)
        .map((k) => normalizeFantasySportsbookId(k))
        .filter(Boolean),
    );
    Object.keys(WEIGHTS_BY_SPORTSBOOK_NAME).forEach((sportsbook) => {
      if (sportsbook !== rowSportsbook && !normalizedComparisonKeys.has(sportsbook)) {
        switch (sportsbook) {
          case "draftkings":
            prop.draftkings_odds = null;
            break;
          case "fanduel":
            prop.fanduel_odds = null;
            break;
          case "caesars":
            prop.caesars_odds = null;
            break;
          case "circa":
            prop.circa_odds = null;
            break;
          case "pinnacle":
            prop.pinnacle_odds = null;
            break;
          case "betmgm":
            prop.betmgm_odds = null;
            break;
          case "fanatics":
            prop.fanatics_odds = null;
            break;
          case "hardrock":
            prop.hardrock_odds = null;
            break;
          case "betr":
            prop.betr_odds = null;
            break;
          case "novig":
            prop.novig_odds = null;
            break;
          case "prophetx":
            prop.prophetx_odds = null;
            break;
          case "dabble":
            prop.dabble_odds = null;
            break;
          case "kalshi":
            prop.kalshi_odds = null;
            break;
          case "draftkingspredictions":
            prop.draftkingspredictions_odds = null;
            break;
          case "kambi":
            prop.kambi_odds = null;
            break;
        }
      }
    });
  }

  if (Object.keys(comparisonLines).length > 0) {
    prop.__comparison_lines = comparisonLines;
  }

  const rawLinks = result.comparison_bet_links ?? {};
  const normalizedLinks: Record<string, BetLinkSet> = {};
  Object.entries(rawLinks).forEach(([book, set]) => {
    if (!set) return;
    const normalizedBook = normalizeFantasySportsbookId(book);
    if (!normalizedBook) return;
    normalizedLinks[normalizedBook] = set;
  });
  if (Object.keys(normalizedLinks).length > 0) {
    prop.__comparison_links = normalizedLinks;
  }

  const rawSelectionIds = result.comparison_selection_ids ?? {};
  const normalizedSelectionIds: Record<string, string> = {};
  Object.entries(rawSelectionIds).forEach(([book, id]) => {
    if (!id) return;
    const normalizedBook = normalizeFantasySportsbookId(book);
    if (!normalizedBook) return;
    normalizedSelectionIds[normalizedBook] = id;
  });
  if (Object.keys(normalizedSelectionIds).length > 0) {
    prop.__comparison_selection_ids = normalizedSelectionIds;
  }
}
